import React from 'react';
import { connect } from 'react-redux';
import Trumbowyg from 'react-trumbowyg';
import { OverlayTrigger, Tooltip, Overlay, Popover, Clearfix } from 'react-bootstrap';
import { forEach as _forEach } from 'lodash';

import IntlFormIndicator from 'components/intlForm/IntlFormIndicator';
import IntlFormSelector from 'components/intlForm/IntlFormSelector';

class FormLangRichEditor extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      // initialValue: props.value || '',
      value: props.value || '',
      focused: false,
      showIntlSelector: false,
    };

    this.inputRef = React.createRef();
  }

  componentDidUpdate = (prevProps, prevState) => {
    const { appIntl } = this.props;
    const { focused, disabled } = this.state;

    if (prevState.focused && !focused) {
      this.timeout = setTimeout(() => this.setState({ showIntlSelector: false }), 300);
    }

    // update content when the language has changed
    if (prevProps.appIntl.displayIntlForm !== appIntl.displayIntlForm) {
      this.languageChanged();
    }

    // trumbowyg gets rid of content when it's disabled, so we need to reinitialize it
    if (prevProps.disabled && !disabled) {
      this.languageChanged();
    }
  };

  componentWillUnmount = () => {
    clearTimeout(this.timeout);
  };

  componentDidMount = () => {
    const { id, name } = this.props;
    $('#' + id + '-trumbowyg-' + name).trumbowyg('html', this.getCurrentValue());
  };

  checkFocused = () => {
    const { focused } = this.state;
    return focused ? 'focused ' : '';
  };

  checkRequired = () => {
    const { meta } = this.props;
    return meta.validate.rules.indexOf('required') !== -1 ? 'required ' : '';
  };

  checkError = () => {
    const { validation } = this.props;
    return validation.valid ? '' : 'has-error ';
  };

  // check if user may read this field
  checkAccess = () => {
    const { meta } = this.props;
    return meta.acl.read;
  };

  // check if user may write to this field
  checkReadOnly = () => {
    const { meta } = this.props;
    return !meta.acl.write;
  };

  catchFocus = () => {
    const { id, name } = this.props;
    const { focused } = this.state;

    if (!focused) {
      $('#' + id + '-trumbowyg-' + name).focus();
    }
  };

  onChange = () => {
    const { id, name, appIntl, langKey, onChange } = this.props;
    const { value } = this.state;

    const newValue = [];
    let isNewValue = true;
    _forEach(value, langValue => {
      if (langValue.lang === appIntl.displayIntlForm) {
        newValue.push({
          ...langValue,
          [langKey]: $('#' + id + '-trumbowyg-' + name).trumbowyg('html'),
        });
        isNewValue = false;
      } else {
        newValue.push({ ...langValue });
      }
    });
    if (isNewValue) {
      newValue.push({
        lang: appIntl.displayIntlForm,
        [langKey]: $('#' + id + '-trumbowyg-' + name).trumbowyg('html'),
      });
    }

    this.setState({ value: newValue });
    clearTimeout(this.timeout);
    this.timeout = setTimeout(() => {
      onChange(name, newValue);
    }, 150);
  };

  checkDisabled = () => {
    const { disabled, meta } = this.props;

    if (disabled) {
      return true;
    }
    if (!meta.acl.write) {
      return true;
    }
    return false;
  };

  setValue = value => {
    const { id, name } = this.props;
    $('#' + id + '-trumbowyg-' + name).trumbowyg('html', value);
    this.onChange(value);
  };

  languageChanged = () => {
    const { id, name } = this.props;
    $('#' + id + '-trumbowyg-' + name).trumbowyg('html', this.getCurrentValue());
  };

  getCurrentValue = () => {
    const { appIntl, langKey } = this.props;
    const { value: stateValue } = this.state;
    let value = '';

    _forEach(stateValue, langName => {
      if (langName.lang === appIntl.displayIntlForm) {
        value = langName[langKey];
      }
    });

    return value;
  };

  render() {
    const { label, name, id, validation, infoText, autogrow } = this.props;
    const { focused, showIntlSelector } = this.state;

    return (
      <div
        className={
          'form-group multi-lang form-group-default ' +
          this.checkFocused() +
          this.checkRequired() +
          this.checkError() +
          (this.checkDisabled() ? 'disabled ' : '')
        }
      >
        <Clearfix>
          <label className="pull-left" onClick={this.catchFocus}>
            {label}
            <IntlFormIndicator show={!showIntlSelector} />
            {!!infoText && (
              <span className="m-l-5">
                <OverlayTrigger placement="top" overlay={<Tooltip>{infoText}</Tooltip>}>
                  <i className="far fa-question-circle cursor-help" />
                </OverlayTrigger>
              </span>
            )}
          </label>
          <div className="pull-left m-l-10">{showIntlSelector && <IntlFormSelector />}</div>
        </Clearfix>
        <div ref={this.inputRef}>
          <Trumbowyg
            id={id + '-trumbowyg-' + name}
            data=""
            onChange={this.onChange}
            onFocus={() => {
              clearTimeout(this.timeout);
              this.setState({ focused: true, showIntlSelector: true });
            }}
            onBlur={() => {
              this.setState({ focused: false });
            }}
            disabled={this.checkDisabled()}
            removeformatPasted
            autogrow={autogrow}
            semantic={false}
            buttons={[
              ['viewHTML'],
              ['formatting'],
              'btnGrp-semantic',
              ['link'],
              'btnGrp-justify',
              'btnGrp-lists',
              ['horizontalRule'],
              ['removeformat'],
            ]}
          />
        </div>

        {!validation.valid && (
          <Overlay show={focused} target={this.inputRef.current} placement="top">
            <Popover className="popover-modal" id={`error_${name}_${label}`}>
              {validation.helpText}
            </Popover>
          </Overlay>
        )}
      </div>
    );
  }
}
FormLangRichEditor.defaultProps = {
  label: '',
  meta: { validate: { rules: '' }, acl: { read: true, write: true } },
  validation: { valid: true },
  value: '',
  onChange: () => {},
  disabled: false,
  langKey: 'description',
  id: '',
  infoText: '',
  autogrow: true,
};

const mapStateToProps = state => {
  return {
    appIntl: state.intl,
  };
};

export default connect(mapStateToProps)(FormLangRichEditor);
